import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Download, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Field, TextInput, SelectInput, TextArea, validateCommon } from "./FormControls";
import { submitProspectus } from "../lib/api";
import { AREAS_OF_INTEREST } from "../data/content";

const EMPTY = { name: "", institution: "", designation: "", email: "", phone: "", area_of_interest: "", message: "" };

export default function ProspectusModal({ open, onOpenChange }) {
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setValues(EMPTY);
      setErrors({});
      setDone(false);
    }
  }, [open]);

  const set = (k) => (e) => setValues((v) => ({ ...v, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    const errs = validateCommon(values, ["name", "institution", "designation", "email", "phone", "area_of_interest"]);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSubmitting(true);
    try {
      await submitProspectus(values);
      setDone(true);
    } catch (err) {
      toast.error("Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-lg" data-testid="prospectus-modal">
        {!done ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl font-bold text-navy">Download the CASIA Prospectus</DialogTitle>
              <DialogDescription className="text-sm text-slate-600">
                Share a few details and the CASIA prospectus will be yours to download. Your enquiry reaches our team
                at info@casia.education.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={onSubmit} noValidate className="mt-2 space-y-4" data-testid="prospectus-form">
              <Field label="Name" htmlFor="prospectus-name" required error={errors.name}>
                <TextInput id="prospectus-name" data-testid="prospectus-name-input" value={values.name} onChange={set("name")} autoComplete="name" />
              </Field>
              <Field label="Institution / School Name" htmlFor="prospectus-institution" required error={errors.institution}>
                <TextInput id="prospectus-institution" data-testid="prospectus-institution-input" value={values.institution} onChange={set("institution")} autoComplete="organization" />
              </Field>
              <Field label="Designation / Role" htmlFor="prospectus-designation" required error={errors.designation}>
                <TextInput id="prospectus-designation" data-testid="prospectus-designation-input" value={values.designation} onChange={set("designation")} placeholder="e.g. Principal, Academic Coordinator" />
              </Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Email" htmlFor="prospectus-email" required error={errors.email}>
                  <TextInput id="prospectus-email" type="email" data-testid="prospectus-email-input" value={values.email} onChange={set("email")} autoComplete="email" />
                </Field>
                <Field label="Phone Number" htmlFor="prospectus-phone" required error={errors.phone}>
                  <TextInput id="prospectus-phone" type="tel" data-testid="prospectus-phone-input" value={values.phone} onChange={set("phone")} autoComplete="tel" />
                </Field>
              </div>
              <Field label="Academic Domain / Area of Interest" htmlFor="prospectus-interest" required error={errors.area_of_interest}>
                <SelectInput id="prospectus-interest" data-testid="prospectus-interest-select" value={values.area_of_interest} onChange={set("area_of_interest")}>
                  <option value="">Select an area of interest</option>
                  {AREAS_OF_INTEREST.map((a) => (
                    <option key={a} value={a}>{a}</option>
                  ))}
                </SelectInput>
              </Field>
              <Field label="Message / Requirements" htmlFor="prospectus-message" error={errors.message}>
                <TextArea id="prospectus-message" data-testid="prospectus-message-input" value={values.message} onChange={set("message")} placeholder="Optional" />
              </Field>
              <button
                type="submit"
                disabled={submitting}
                data-testid="prospectus-submit-button"
                className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-navy px-4 py-3 text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-navy-700 disabled:opacity-60"
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                {submitting ? "Submitting…" : "Submit & Continue"}
              </button>
            </form>
          </>
        ) : (
          <div className="py-6 text-center" data-testid="prospectus-success">
            <CheckCircle2 className="mx-auto h-14 w-14 text-gold" aria-hidden="true" />
            <DialogHeader className="mt-4">
              <DialogTitle className="font-serif text-2xl font-bold text-navy">
                Thank you for your interest in CASIA.
              </DialogTitle>
              <DialogDescription className="mx-auto max-w-sm text-sm text-slate-600">
                Your details have been shared with our team. You can now download the CASIA prospectus.
              </DialogDescription>
            </DialogHeader>
            <a
              href="/assets/CASIA%20Prospectus.pdf"
              download="CASIA Prospectus.pdf"
              data-testid="download-casia-prospectus-button"
              className="mt-6 inline-flex items-center gap-2 rounded-md bg-gold px-6 py-3.5 text-sm font-bold uppercase tracking-wider text-navy transition-colors hover:bg-gold-hover"
            >
              <Download className="h-4 w-4" aria-hidden="true" />
              Download CASIA Prospectus
            </a>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
